import {randomUUID} from '@github/browser-support/lib/crypto-randomuuid'
import {memo, useCallback, useEffect, useMemo, useRef, useState} from 'react'

import type {PersistedOption} from '../../api/columns/contracts/single-select'
import {apiCreateMemexStatus} from '../../api/memex/api-create-status'
import {apiGetMemexStatuses} from '../../api/memex/api-get-statuses'
import type {MemexStatus} from '../../api/memex/contracts'
import {getInitialState} from '../../helpers/initial-state'
import {formatISODateString} from '../../helpers/parsing'
import {useEnabledFeatures} from '../../hooks/use-enabled-features'
import {useSidePanel} from '../../hooks/use-side-panel'
import {type StatusUpdate, StatusUpdatesContext} from './status-updates-context'

export const StatusUpdatesProvider = memo(function StatusUpdatesProvider({children}: {children: React.ReactNode}) {
  const {loggedInUser} = getInitialState()
  const {sidePanelState} = useSidePanel()
  const {memex_status_updates} = useEnabledFeatures()
  const [statusUpdates, setStatusUpdates] = useState<Array<MemexStatus>>([])
  const [statusOptions, setStatusOptions] = useState<Array<PersistedOption>>([])
  const [isAdding, setIsAdding] = useState(false)
  const fetchingRef = useRef(false)

  const statusOptionsMap = useMemo(() => {
    return new Map(statusOptions.map(option => [option.id, option]))
  }, [statusOptions])

  // Only fetch when the 'info' panel is opened
  const sidePanelOpen = sidePanelState?.type === 'info'

  useEffect(() => {
    if (fetchingRef.current || !sidePanelOpen || !memex_status_updates) {
      return
    }

    fetchingRef.current = true

    apiGetMemexStatuses().then(response => {
      setStatusUpdates(response.statuses)
      setStatusOptions(response.form.status.options)

      // For now, as live-updates are unavailable, refetch everytime the info panel is opened.
      fetchingRef.current = false
    })
  }, [memex_status_updates, setStatusUpdates, sidePanelOpen])

  const addStatusUpdate = useCallback(
    async ({body, startDate, targetDate, status}: StatusUpdate) => {
      const statusOption = statusOptionsMap && status && statusOptionsMap.get(status.id)

      if (!statusOption) {
        throw new Error('Unable to determine status option')
      }

      if (!loggedInUser) {
        throw new Error('Must be logged in to create a status update')
      }

      const tempId = randomUUID()

      const optimisticStatusUpdate: MemexStatus = {
        id: tempId,
        body,
        updatedAt: new Date().toISOString(),
        creator: loggedInUser,
        statusValue: {
          startDate: startDate ? formatISODateString(startDate) : '',
          targetDate: targetDate ? formatISODateString(targetDate) : '',
          statusId: status?.id ?? '',
          status: statusOption,
        },
        bodyHtml: body,
      }

      setStatusUpdates(statuses => [optimisticStatusUpdate, ...statuses])

      try {
        const response = await apiCreateMemexStatus({
          body,
          startDate: startDate ? formatISODateString(startDate) : '',
          targetDate: targetDate ? formatISODateString(targetDate) : '',
          statusId: status?.id ?? null,
        })

        setStatusUpdates(statuses => [response, ...statuses.filter(s => s.id !== tempId)])
      } catch {
        setStatusUpdates(statuses => statuses.filter(s => s.id !== tempId))
      }
    },
    [loggedInUser, statusOptionsMap],
  )

  return (
    <StatusUpdatesContext.Provider
      value={useMemo(() => {
        return {
          statusUpdates,
          statusOptions,
          addStatusUpdate,
          isAdding,
          setIsAdding,
        }
      }, [addStatusUpdate, isAdding, statusOptions, statusUpdates])}
    >
      {children}
    </StatusUpdatesContext.Provider>
  )
})

try{ StatusUpdatesProvider.displayName ||= 'StatusUpdatesProvider' } catch {}